import { SlideUpdate } from 'api/slides';
import { CaseUpdate } from 'api/study';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { filter, find, flatMap, get, includes, isArray, isEmpty, keys, map, omit, size, some } from 'es-toolkit/compat';
import {
  getBaseRowIdFromUnwoundRowMetadata,
  getInnerRowIdFromUnwoundRowMetadata,
  unwindRows,
  UnwoundRow,
  unwoundRowMetadataFromId,
} from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import pluralize from 'pluralize';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';

import { getProcedureChanges, getSlideChanges } from '../ProceduresPage/DataGrids/useBulkEditControl';
import { getPageCasesInSlidesMode } from './casePagination';
import { ManifestDialogUrlState } from './dialogState';
import { applyManifestCaseEdits, applyManifestSlideEdits } from './manifestDraft';
import { filterCasesWithErrors } from './manifestResolution';

interface ManifestPlanEditingOptions {
  currentDraftCases: Procedure[];
  baseManifestCases: Procedure[];
  baseResolvedManifestCases: Procedure[];
  fields: Parameters<typeof filterCasesWithErrors>[0]['fields'];
  procedureFieldContext: Parameters<typeof filterCasesWithErrors>[0]['procedureFieldContext'];
  shouldValidateManifestDraft: boolean;
  showOnlyErrors: boolean;
  showOnlyUnassignedSlides: boolean;
  unassignedSlideIds: string[];
  page: number;
  rowsPerPage: number;
  isCaseLabelEditable: (slideId: string) => boolean;
  updateManifestDialogUrlState: (
    updates: Partial<ManifestDialogUrlState> | ((previousState: ManifestDialogUrlState) => ManifestDialogUrlState)
  ) => void;
  setCaseManifestSelectedRows: (rows: string[], updateType: 'replaceIn') => void;
  setCaseManifestOmittedRows: (rows: string[], updateType: 'replaceIn') => void;
}

export const useManifestPlanEditing = ({
  currentDraftCases,
  baseManifestCases,
  baseResolvedManifestCases,
  fields,
  procedureFieldContext,
  shouldValidateManifestDraft,
  showOnlyErrors,
  showOnlyUnassignedSlides,
  unassignedSlideIds,
  page,
  rowsPerPage,
  isCaseLabelEditable,
  updateManifestDialogUrlState,
  setCaseManifestSelectedRows,
  setCaseManifestOmittedRows,
}: ManifestPlanEditingOptions) => {
  const { selectionMode, selectedRows, omittedRows, setRangeSelectionOrder } = useRowSelectionContext();
  const displayedManifestCasesUnwoundSlides = useMemo(
    () =>
      unwindRows<Procedure, Slide>({
        rows: currentDraftCases || [],
        arrayFieldToUnwind: 'slides',
        unwoundRowIdField: 'id',
        omitUnwoundFields: ['cancerTypeId'],
      }) as UnwoundRow<Procedure, Slide>[],
    [currentDraftCases]
  );
  useEffect(() => {
    setRangeSelectionOrder(map(displayedManifestCasesUnwoundSlides, (row) => row.id));
  }, [displayedManifestCasesUnwoundSlides, setRangeSelectionOrder]);
  useEffect(() => {
    const rowIdsBySlideId = new Map(
      map(displayedManifestCasesUnwoundSlides, (row) => [String(row._unwoundRowMetadata.innerRowId), String(row.id)])
    );
    const remapRows = (rowIds: Array<string | number>) =>
      map(rowIds, (rowId) => {
        const { innerRowId } = unwoundRowMetadataFromId(String(rowId));
        return rowIdsBySlideId.get(String(innerRowId)) || String(rowId);
      });
    const nextSelectedRows = remapRows(selectedRows);
    const nextOmittedRows = remapRows(omittedRows);
    if (some(nextSelectedRows, (rowId, index) => rowId !== selectedRows[index])) {
      setCaseManifestSelectedRows(nextSelectedRows, 'replaceIn');
    }
    if (some(nextOmittedRows, (rowId, index) => rowId !== omittedRows[index])) {
      setCaseManifestOmittedRows(nextOmittedRows, 'replaceIn');
    }
  }, [
    displayedManifestCasesUnwoundSlides,
    selectedRows,
    omittedRows,
    setCaseManifestSelectedRows,
    setCaseManifestOmittedRows,
  ]);
  const baseDisplayCasesUnwoundSlides = useMemo(
    () =>
      unwindRows<Procedure, Slide>({
        rows: baseResolvedManifestCases || [],
        arrayFieldToUnwind: 'slides',
        unwoundRowIdField: 'id',
        omitUnwoundFields: ['cancerTypeId'],
      }) as UnwoundRow<Procedure, Slide>[],
    [baseResolvedManifestCases]
  );

  const applyCellValueChangedClass = (id: string | number, field: string) => {
    const existingValue = get(find(displayedManifestCasesUnwoundSlides, { id }), field);
    let oldValue;
    if (typeof id === 'string') {
      const { innerRowId: slideId } = unwoundRowMetadataFromId(id);
      oldValue = get(
        find(baseDisplayCasesUnwoundSlides, (row) => row?._unwoundRowMetadata?.innerRowId === slideId),
        field
      );
    } else {
      oldValue = get(find(baseDisplayCasesUnwoundSlides, { id }), field);
    }
    const oldValueIsNoneOrEmpty = !oldValue || (isArray(oldValue) && isEmpty(oldValue));
    const existingValueIsNoneOrEmpty = !existingValue || (isArray(existingValue) && isEmpty(existingValue));
    return oldValueIsNoneOrEmpty && existingValueIsNoneOrEmpty ? false : oldValue !== existingValue;
  };

  const onBulkSlideDataChange = (changes: Partial<SlideUpdate>, slideIds: string[]) => {
    updateManifestDialogUrlState((previousState) => applyManifestSlideEdits(previousState, changes, slideIds));
  };
  const onBulkCaseDataChange = (changes: Partial<CaseUpdate>, caseIds: number[], slideId?: string) => {
    updateManifestDialogUrlState((previousState) =>
      applyManifestCaseEdits({
        previousState,
        changes,
        caseIds,
        slideId,
        currentDraftCases,
        baseManifestCases,
        isCaseLabelEditable,
      })
    );
  };
  const onCaseDataChange = (caseId: number, changes: Partial<CaseUpdate>, slideId?: string) => {
    onBulkCaseDataChange(changes, [caseId], slideId);
  };
  const onSlideDataChange = (slideId: string, changes: Partial<SlideUpdate>) => {
    onBulkSlideDataChange(changes, [slideId]);
  };

  const casesWithError = useMemo(
    () =>
      shouldValidateManifestDraft
        ? filterCasesWithErrors({ cases: currentDraftCases, fields, procedureFieldContext })
        : [],
    [currentDraftCases, fields, procedureFieldContext, shouldValidateManifestDraft]
  );
  const unassignedSlideIdSet = useMemo(() => new Set(unassignedSlideIds), [unassignedSlideIds]);
  const manifestCasesToDisplay = useMemo(() => {
    const cases = showOnlyErrors ? casesWithError : currentDraftCases;
    if (!showOnlyUnassignedSlides) return cases;
    return flatMap(cases, (procedure) => {
      const slides = filter(procedure.slides || [], (slide) => unassignedSlideIdSet.has(slide.id));
      return isEmpty(slides) ? [] : [{ ...procedure, slides }];
    });
  }, [casesWithError, currentDraftCases, showOnlyErrors, showOnlyUnassignedSlides, unassignedSlideIdSet]);
  const visibleManifestCasesUnwoundSlides = useMemo(
    () =>
      unwindRows<Procedure, Slide>({
        rows: manifestCasesToDisplay,
        arrayFieldToUnwind: 'slides',
        unwoundRowIdField: 'id',
        omitUnwoundFields: ['cancerTypeId'],
      }) as UnwoundRow<Procedure, Slide>[],
    [manifestCasesToDisplay]
  );
  const visibleSlideIds = useMemo(
    () => new Set(map(flatMap(manifestCasesToDisplay, 'slides'), 'id')),
    [manifestCasesToDisplay]
  );
  const visibleCaseIds = useMemo(() => new Set(map(manifestCasesToDisplay, 'id')), [manifestCasesToDisplay]);
  const onBulkEditSave = (changes: { [field: string]: any }) => {
    const slidesChanges = getSlideChanges(changes);
    if (!isEmpty(keys(slidesChanges))) {
      if (selectionMode === 'select') {
        onBulkSlideDataChange(
          slidesChanges,
          filter(getInnerRowIdFromUnwoundRowMetadata(selectedRows as string[]), (slideId) =>
            visibleSlideIds.has(slideId)
          )
        );
      } else {
        const omittedSlideIds = getInnerRowIdFromUnwoundRowMetadata(omittedRows as string[]);
        const selectedSlideIds = filter(
          map(flatMap(manifestCasesToDisplay, 'slides'), 'id'),
          (slideId) => !includes(omittedSlideIds, slideId)
        );
        onBulkSlideDataChange(slidesChanges, selectedSlideIds);
      }
    }

    const procedureChanges = omit(getProcedureChanges(changes), ['label']);
    if (!isEmpty(keys(procedureChanges))) {
      if (selectionMode === 'select') {
        const selectedCaseIds = filter(
          map(getBaseRowIdFromUnwoundRowMetadata(selectedRows as string[]), Number),
          (caseId) => visibleCaseIds.has(caseId)
        );
        onBulkCaseDataChange(procedureChanges, selectedCaseIds);
      } else {
        const omittedCaseIds = map(getBaseRowIdFromUnwoundRowMetadata(omittedRows as string[]), Number);
        const selectedCaseIds = filter(
          map(manifestCasesToDisplay, 'id'),
          (caseId) => !includes(omittedCaseIds, caseId)
        );
        onBulkCaseDataChange(procedureChanges, selectedCaseIds);
      }
    }
  };
  const onShowOnlyErrorsSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateManifestDialogUrlState({ showOnlyErrors: Boolean(event.target.checked) });
  };
  const pageCases = getPageCasesInSlidesMode(manifestCasesToDisplay, page, rowsPerPage);
  const totalSlides = flatMap(manifestCasesToDisplay, (updatedCase) => updatedCase.slides).length;
  const totalCases = size(manifestCasesToDisplay);
  const onPageChange = (withOffset: boolean) => (_event: ChangeEvent<unknown>, nextPage?: number) => {
    if (nextPage == null) return;
    const nextPageWithOffset = withOffset ? nextPage + 1 : nextPage;
    if (page !== nextPageWithOffset) updateManifestDialogUrlState({ page: nextPageWithOffset });
  };
  const onRowsPerPageChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newPageSize = parseInt(event.target.value) ?? DEFAULT_PAGE_SIZE;
    if (newPageSize !== rowsPerPage) updateManifestDialogUrlState({ rowsPerPage: newPageSize, page: 1 });
  };

  const editableManifestCaseIds = useMemo(() => map(currentDraftCases, 'id'), [currentDraftCases]);
  const editableManifestSlideIds = useMemo(() => map(flatMap(currentDraftCases, 'slides'), 'id'), [currentDraftCases]);
  const selectedManifestSlides = useMemo(() => {
    const editableDisplayedManifestRows = filter(
      visibleManifestCasesUnwoundSlides,
      (row) =>
        Boolean(row?._unwoundRowMetadata?.innerRowId) &&
        includes(editableManifestSlideIds, row._unwoundRowMetadata.innerRowId)
    );
    const isRowSelected = (row: UnwoundRow<Procedure, Slide>, rowIds: Array<string | number>) =>
      includes(rowIds, row.id) ||
      includes(rowIds, row?._unwoundRowMetadata?.baseRowId) ||
      includes(rowIds, String(row?._unwoundRowMetadata?.baseRowId));
    return selectionMode === 'select'
      ? filter(editableDisplayedManifestRows, (row) => isRowSelected(row, selectedRows))
      : filter(editableDisplayedManifestRows, (row) => !isRowSelected(row, omittedRows));
  }, [visibleManifestCasesUnwoundSlides, editableManifestSlideIds, selectionMode, selectedRows, omittedRows]);
  const selectedManifestSlideIds = useMemo(
    () => Array.from(new Set(map(selectedManifestSlides, (row) => String(row._unwoundRowMetadata.innerRowId)))),
    [selectedManifestSlides]
  );
  const selectedManifestSlidesCount = selectedManifestSlideIds.length;
  const selectedManifestSlidesRemoveButtonText =
    selectedManifestSlidesCount === 0
      ? 'Leave selected slides out of this assignment'
      : `Leave ${pluralize('selected slide', selectedManifestSlidesCount, true)} out of this assignment`;
  const removeSelectedManifestSlidesFromManifest = useCallback(() => {
    if (isEmpty(selectedManifestSlideIds)) return;
    updateManifestDialogUrlState((previousState) => ({
      ...previousState,
      removedSlideIds: Array.from(new Set([...(previousState.removedSlideIds || []), ...selectedManifestSlideIds])),
    }));
  }, [selectedManifestSlideIds, updateManifestDialogUrlState]);

  return {
    applyCellValueChangedClass,
    editableManifestCaseIds,
    editableManifestSlideIds,
    onBulkEditSave,
    onCaseDataChange,
    onPageChange,
    onRowsPerPageChange,
    onShowOnlyErrorsSwitchChange,
    onSlideDataChange,
    pageCases,
    removeSelectedManifestSlidesFromManifest,
    selectAllManifestSlides: () =>
      setCaseManifestSelectedRows(
        map(visibleManifestCasesUnwoundSlides, (row) => String(row.id)),
        'replaceIn'
      ),
    selectedManifestSlideIds,
    selectedManifestSlidesCount,
    selectedManifestSlidesRemoveButtonText,
    totalCases,
    totalSlides,
  };
};
